import VisualizadorPdfComponent from '@/components/visualizador-pdf/visualizador-pdf.vue';
import GenerarPdfService from '@/entities/evaluacion/generarPdf/generar-pdf.service';
import { EstadoSolicitud } from '@/shared/model/enumerations/estado-solicitud.model';
import { EstadoSolucion } from '@/shared/model/enumerations/estado-solucion.model';
import { TipoSolucion } from '@/shared/model/enumerations/tipo-solucion.model';
import { Filtro } from '@/shared/model/filtro.model';
import { ComprobantePdf, Solicitud } from '@/shared/model/solicitud.model';
import { BForm, BModal, BvModalEvent } from 'bootstrap-vue';
import Component, { mixins } from 'vue-class-component';
import { Inject, Prop, Ref } from 'vue-property-decorator';
import SolicitudMixin from '../mixins/solicitud-mixin';

@Component({
  components: {
    'visualizador-pdf': VisualizadorPdfComponent,
  },
})
export default class ComprobanteComponent extends mixins(SolicitudMixin) {
  @Inject('generarPdfService') private readonly generarPdfService: () => GenerarPdfService;

  @Prop({ required: true })
  public solicitud: Solicitud;

  public comprobantePdf: ComprobantePdf;

  public solicitudProp!: Solicitud;

  public showPdf = false;
  public pdfReady = false;
  public pdfFirma = false;
  public contenidoBase64 = '';

  public cuentaIngresada = '';

  public cuentaGuardada = null;

  public vistaPreviaGenerada = false;

  public cuentaValida = false;

  @Ref('form')
  public readonly forms: BForm;

  @Ref('modal')
  public readonly modal: BModal;

  /**
   * CVU del solicitante
   */
  public cvuSolicitante = '';

  /**
   * Contraseña para crear la firma del pdf
   */
  public password = '';

  /**
   * Indica si se debe mostrar la contraseña o no.
   */
  public mostrarPassword = false;

  /**
   * Indica si se debe mostrar la leyenda de que el campo es requerido.
   */
  public estadoPassword = false;

  public isFetching = false;

  private estadoAnterior: string | null = null;

  /**
   * Indica que en esta sesión ya se firmó correctamente estando en edición.
   */
  private firmaVigenteEnEdicion = false;

  mounted(): void {
    /**
     * Al montar el componente, reinicia el flag.
     */
    this.firmaVigenteEnEdicion = false;

    this.get();
    this.getSolucionesCuentasBancarias();
    this.consultarComprobante();
  }

  created() {
    this.solicitudProp = this.solicitud;
  }

  /**
   * Carga inicial del componente
   *
   * @memberof ComprobanteComponent
   */
  public get(): void {
    if (this.solicitud?.comprobantePdf) {
      this.comprobantePdf = this.solicitud.comprobantePdf;
    }
  }

  /**
   * Filtra soluciones por tipo CUENTAS_BANCARIAS y estado de la solución.
   */
  public getSolucionesCuentasBancarias(): void {
    const filtro = new Filtro();
    filtro.estadoSolucion = EstadoSolucion.PUBLICADA;
    filtro.tiposSolucion = [TipoSolucion.CUENTAS_BANCARIAS_SNII_INV, TipoSolucion.CUENTAS_BANCARIAS_SNII_AYU];

    this.buscarIdSoluciones(filtro);
  }

  /**
   * Mostrar "Ver comprobante" solo si hay URI y NO está en edición.
   */
  get mostrarButtonVerComprobante() {
    return (
      this.solicitud?.comprobantePdf?.uri &&
      this.solicitud?.estado !== EstadoSolicitud.EN_CAPTURA &&
      this.solicitud?.estado !== EstadoSolicitud.EN_ACTUALIZACION
    );
  }

  /**
   * Valida si existe la URL del comprobante
   */
  get tieneUrl() {
    return this.solicitud?.comprobantePdf?.uri;
  }
  /**
   * Mostrar botón de "Vista previa/Firmar" en CUENTAS_BANCARIAS:
   * - Si está en edición => mostrar SOLO si aún NO se ha firmado correctamente en esta sesión.
   * - Si NO está en edición => mostrar si NO existe URI.
   */
  public mostrarButtonGenerarPdfComprobanteCuentaBancaria() {
    if (this.esEditable) {
      return !this.firmaVigenteEnEdicion;
    }
    return !this.solicitud?.comprobantePdf?.uri;
  }

  /**
   * Determina si el botón debe mostrarse para la vista previa o firmar el pdf de eméritos y 65 + 15.
   *
   * @memberof ComprobanteComponent
   */
  public mostrarButtonGenerarPdfComprobanteExcepciones() {
    return this.solicitud?.estado === EstadoSolicitud.EN_FIRMA && !this.solicitud?.comprobantePdf?.uri;
  }

  /**
   * Determina si el botón debe mostrarse para la vista previa o firmar el pdf
   *
   * @memberof ComprobanteComponent
   */
  get mostrarBotonGenerar() {
    if (this.isCuentaBancaria) {
      return this.mostrarButtonGenerarPdfComprobanteCuentaBancaria();
    }
    return this.mostrarButtonGenerarPdfComprobanteExcepciones();
  }

  /**
   * Determina si el texto debe mostrarse para eméritos y 65 + 15.
   *
   * @memberof ComprobanteComponent
   */
  get mostrarTextoGenerarPdfComprobante() {
    const estados = [EstadoSolicitud.EN_FIRMA, EstadoSolicitud.FIRMADA, EstadoSolicitud.FINALIZADA];
    return estados.includes(this.solicitud.estado);
  }

  /**
   * Establece el password para generar un documento de una solicitud
   *
   * @memberof ComprobanteComponent
   */
  public establecerPasswordDocumento(): void {
    this.modal.show();
  }

  /**
   * Nuevo: Variable para agrupar lógica de estados editables
   */
  get esEditable(): boolean {
    return this.solicitud?.estado === 'EN_CAPTURA' || this.solicitud?.estado === 'EN_ACTUALIZACION';
  }

  /**
   * Genera el documento pdf y lo muestra en el visualizador-pdf
   *
   * @memberof ComprobanteComponent
   */
  public async generarVisualizar() {
    let responseDocumento;
    try {
      this.showPdf = true;
      this.pdfReady = false;
      if (this.isCuentaBancaria) {
        responseDocumento = await this.generarPdfService().exportarComprobante(this.solicitud.id, null, this.solicitud.solicitante.cvu);
      } else {
        responseDocumento = await this.generarPdfService().exportarComprobanteAnual(
          this.solicitud.id,
          null,
          this.solicitud.solicitante.cvu
        );
      }

      this.contenidoBase64 = `data:application/pdf;base64,${responseDocumento.contenidoBase64}`;
      this.showPdf = false;
      this.pdfReady = true;
      if (this.isCuentaBancaria) {
        const solicitudVistaPreviaActiva = {
          ...this.solicitud,
          vistaPreviaActiva: true,
        };

        const solicitudActualizada = await this.solicitudService().partialUpdate(solicitudVistaPreviaActiva as any);

        this.solicitud = solicitudActualizada;

        this.validaVistaPreviaActiva();

        return solicitudActualizada;
      }
    } catch (error) {
      this.showPdf = false;
      this.alertService().showHttpError(this, error.response);
      return Promise.reject(error);
    } finally {
      this.showPdf = false;
    }
  }

  /**
   * Se valida si la solicitud pertenece a una solucion de tipo cuenta bancaria.
   *
   * @memberof ComprobanteComponent
   */
  get isCuentaBancaria() {
    return this.solucionesId.some(id => id === this.solicitud.solucionId);
  }

  /**
   * Genera documento PDF.
   *
   * @memberof ComprobanteComponent
   */
  public generar(bvModalEvent: BvModalEvent): void {
    bvModalEvent.preventDefault();
    const valido = this.forms.checkValidity();
    this.estadoPassword = valido;
    if (!valido) {
      return;
    }
    this.$nextTick(() => {
      this.modal.hide();
    });

    this.showPdf = true;
    this.generarDocumento();
    this.showPdf = false;
    this.cvuSolicitante = '';
    this.estadoPassword = null;
  }

  /**
   * Método para generar un comprobante de una solcitud en formato PDF.
   *
   * @memberof ComprobanteComponent
   */
  public async generarDocumento() {
    this.pdfFirma = true;

    try {
      let solicitudActualizada;

      if (this.isCuentaBancaria) {
        solicitudActualizada = await this.generarPdfService().exportarComprobante(
          this.solicitud.id,
          this.password,
          this.solicitud.solicitante.cvu
        );
      } else {
        solicitudActualizada = await this.generarPdfService().exportarComprobanteAnual(
          this.solicitud.id,
          this.password,
          this.solicitud.solicitante.cvu
        );
      }

      // Actualizas el estado local
      this.solicitud = solicitudActualizada;
      this.comprobantePdf = solicitudActualizada.comprobantePdf;

      if (this.esEditable && this.isCuentaBancaria) {
        this.firmaVigenteEnEdicion = true;
        await this.solicitudEnviada(this.solicitud);
        this.solicitudProp.estado = EstadoSolicitud.ENVIADA;
        this.doCelebrate();
        this.consultarComprobante();
      } else {
        await this.solicitudEnFirma(this.solicitud);
        this.solicitudProp.estado = EstadoSolicitud.FIRMADA;
      }
    } catch (error) {
      this.pdfFirma = false;
    } finally {
      this.pdfFirma = false;
    }
  }

  // Inicia la animación de confeti por 5 segundos
  public doCelebrate() {
    (this as any).$confetti.start();
    setTimeout(() => {
      (this as any).$confetti.stop();
    }, 5000);
  }
  public previousState(): void {
    this.$router.go(-1);
  }

  /**
   * Consulta la solicitud para refrescar comprobante.
   * Importante: ya NO limpiamos la URI en front.
   */
  public consultarComprobante(): void {
    this.pdfFirma = true;
    this.solicitudService()
      .find(this.solicitud.id)
      .then(res => {
        this.estadoAnterior = this.solicitud.estado;
        this.solicitud = res;
        this.comprobantePdf = res.comprobantePdf;
        this.pdfFirma = false;
      })
      .catch(error => {
        this.pdfFirma = false;
        this.alertService().showHttpError(this, error.response);
        throw error;
      })
      .finally(() => {
        this.pdfFirma = false;
      });
  }

  /**
   * Mensaje UX basado en estado actual
   */
  get mensajeEstadoActual(): string {
    const estado = this.solicitud?.estado;

    if (!this.isCuentaBancaria) return '';

    if (this.esEditable && this.tieneCuenta) {
      if (this.tieneCuenta && !this.reglasValidas) {
        return this.$t(
          'Como parte del proceso para la genereracion del comprobante se requiere capture nuevamente su cuenta CLABE'
        ) as string;
      }

      if (!this.vistaPreviaGenerada && this.reglasValidas) {
        return this.$t('Para continuar con el proceso de firma del comprobante, primero deberá visualizar el documento.') as string;
      }
      // No hay firma vigente en esta sesión
      if (this.vistaPreviaGenerada && !this.tieneUrl) {
        return this.$t('apeironGwApp.comprobante.noFirmado') as string;
      }
    }

    // Estados no editables
    if (estado === 'ENVIADA') {
      return this.$t('apeironGwApp.comprobante.enviado') as string;
    }
    if (estado === 'CANCELADA') {
      return this.$t('apeironGwApp.comprobante.cancelada') as string;
    }

    return '';
  }

  get mensajeEstadoVariant(): string {
    const estado = this.solicitud?.estado;

    if (this.isCuentaBancaria) {
      if (this.esEditable) {
        // En edición: el variant depende solo del flag local
        if (this.tieneUrl) {
          return 'info';
        }
        return 'warning';
      }
      if (estado === 'ENVIADA') {
        return 'success';
      }
      if (estado === 'CANCELADA') {
        return 'danger';
      }
      return 'info';
    }
    return '';
  }

  /**
   * Verifica que la cuentaBancaria  esté guardada en DB
   */
  get tieneCuenta() {
    return this.solicitud?.cuentaBancaria;
  }

  /**
   * Verifica que la propieda vistaPreviaActiva esté guardada en DB
   */
  public validaVistaPreviaActiva() {
    this.vistaPreviaGenerada = !!this.solicitud?.vistaPreviaActiva;
  }

  /**
   * Verifica que la cuenta ingresada sea la misma a la que está guardada en DB
   */
  public async validarCuenta() {
    this.cuentaIngresada = this.cuentaIngresada.replace(/\D/g, '');

    if (this.solicitud?.cuentaBancaria === this.cuentaIngresada) {
      this.cuentaValida = true;

      try {
        await this.validaReglas(this.solicitud);
      } catch (e) {
        // ya se muestran los errores en  los avisos
      }
    } else {
      this.cuentaValida = false;
      this.vistaPreviaGenerada = false;
    }
  }

  /**
   * Valida que solo ingresen números
   *
   * @param event
   */
  public soloNumeros(event) {
    const charCode = event.charCode;

    // Solo permitir números (0-9)
    if (charCode < 48 || charCode > 57) {
      event.preventDefault();
    }
  }
}
